import { useCallback, useEffect, useState } from "react";
import {
  useGetKnowledgeBaseAttachmentWithContentQuery,
  useUpdateKnowledgeBaseAttachmentContentMutation,
} from "../../../api/entities/knowledgeBase.js";

interface UseAttachmentEditorStateProps {
  knowledgeBaseId: string;
  attachmentId: string;
}

export function useAttachmentEditorState({
  knowledgeBaseId,
  attachmentId,
}: UseAttachmentEditorStateProps) {
  const { currentData: attachment, isLoading } =
    useGetKnowledgeBaseAttachmentWithContentQuery({
      knowledgeBaseId,
      attachmentId,
    });
  const [updateContent, { isLoading: isSaving }] =
    useUpdateKnowledgeBaseAttachmentContentMutation();

  const [editedBody, setEditedBody] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  useEffect(() => {
    if (attachment && !isInitialized) {
      setEditedBody(attachment.body ?? "");
      setIsInitialized(true);
    }
  }, [attachment, isInitialized]);

  const fetchedBody = attachment?.body ?? "";
  const hasUnsavedChanges = isInitialized && editedBody !== fetchedBody;

  const onSave = useCallback(async () => {
    await updateContent({
      knowledgeBaseId,
      attachmentId,
      body: editedBody,
    }).unwrap();
  }, [updateContent, knowledgeBaseId, attachmentId, editedBody]);

  const onDiscard = useCallback(() => {
    setEditedBody(fetchedBody);
  }, [fetchedBody]);

  return {
    attachment,
    isLoading,
    isSaving,
    isInitialized,
    editedBody,
    setEditedBody,
    fetchedBody,
    hasUnsavedChanges,
    onSave,
    onDiscard,
    selectedTabIndex,
    setSelectedTabIndex,
  };
}
